import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";

/** Dev-only diagnostics (disabled in production builds). */
export function authDebugLog(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.log("[auth]", ...args);
  }
}

const refreshInFlight = new WeakMap<
  SupabaseClient,
  ReturnType<SupabaseClient["auth"]["refreshSession"]>
>();

/**
 * Single-flight refresh per Supabase client (prevents parallel refreshSession storms).
 */
export async function safeRefresh(client: SupabaseClient = supabase) {
  let p = refreshInFlight.get(client);
  if (!p) {
    authDebugLog("refreshSession (mutex) start");
    p = client.auth.refreshSession().finally(() => {
      refreshInFlight.delete(client);
      authDebugLog("refreshSession (mutex) end");
    });
    refreshInFlight.set(client, p);
  }
  return p;
}

/** True if JWT exp is missing, invalid, or within skewMs of expiry (APIs return 401 on expired user JWTs). */
export function accessTokenNeedsRefresh(token: string, skewMs = 120_000): boolean {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return true;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
    const { exp } = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof exp !== "number") return true;
    return exp * 1000 <= Date.now() + skewMs;
  } catch {
    return true;
  }
}

/**
 * Valid user access_token after refresh if needed, or null (guest / unusable session).
 * All refresh operations go through safeRefresh().
 */
export async function getFreshUserAccessToken(client: SupabaseClient = supabase): Promise<string | null> {
  const {
    data: { session: s0 },
  } = await client.auth.getSession();
  if (!s0) {
    return null;
  }
  let token = s0.access_token ?? null;
  if (token && !accessTokenNeedsRefresh(token)) {
    return token;
  }
  const { data: refreshed, error } = await safeRefresh(client);
  if (error) {
    authDebugLog("safeRefresh error", error.message);
    return null;
  }
  token = refreshed.session?.access_token ?? null;
  if (token && !accessTokenNeedsRefresh(token, 0)) {
    return token;
  }
  return null;
}

/**
 * Local session snapshot (React bootstrap / polling only). Do not use for Authorization headers — use getFreshUserAccessToken.
 */
export async function getLocalSession(client: SupabaseClient = supabase): Promise<Session | null> {
  const {
    data: { session },
  } = await client.auth.getSession();
  return session ?? null;
}
